import { ROLES, type PublicUser, type Role } from '@ghp/domain'
import type { BadgeTone } from '@ghp/ui'

import type { ReadFailureNotice } from '../../../lib/read-failure'

/**
 * Les libellés d'écran des cinq rôles, et le ton de leur pastille — repris du prototype validé
 * (`ROLE_CLS` de `mocks/ghp-bo/ext.js` : rouge pour l'administrateur, bleu pour le pilotage).
 *
 * ⚠️ **Ce sont des libellés, pas les valeurs de l'énumération** : le code manipule `back_office`,
 * l'écran affiche « Back-office ». La liste fermée vit dans `@ghp/domain` (`ROLES`) et le
 * diagramme fait foi sur les valeurs — cette table ne fait que les habiller.
 *
 * ⚠️ **Le prototype distingue « Pilotage » et « Traitement », le modèle non** : les deux sont
 * `back_office`, et ce qui les sépare — un jeu de modules par personne — n'existe nulle part
 * dans le modèle. C'est [Q9], et c'est pourquoi cet écran n'affiche pas la matrice de droits du
 * prototype. En attendant, un compte de bureau s'annonce « Back-office » : inventer l'un des deux
 * libellés afficherait un périmètre que rien ne fait respecter.
 *
 * ⚠️ `rh` reçoit ici « Pilotage / RH », le libellé que le prototype donne à Géraldine Martin.
 * C'est la seule trace d'un périmètre pour ce rôle, et elle n'est **pas** une décision — [Q9]
 * reste ouverte, et c'est à GHP de la trancher.
 *
 * [Q9]: ../../../../../../docs/MODELE-DONNEES.md#q9
 */
export const ROLE_PRESENTATION: Record<Role, { label: string; tone: BadgeTone }> = {
  admin: { label: 'Administrateur', tone: 'bad' },
  back_office: { label: 'Back-office', tone: 'info' },
  rh: { label: 'Pilotage / RH', tone: 'info' },
  agent: { label: 'Agent', tone: 'ok' },
  team: { label: 'Équipe', tone: 'info' },
}

/** L'état d'un compte — « verrouillé » est le mot du prototype, et il dit bien que rien n'est
 * supprimé : la ligne reste, c'est la connexion qui est refusée (M6). */
export const ACCOUNT_STATE_PRESENTATION: Record<
  'open' | 'locked',
  { label: string; tone: BadgeTone }
> = {
  open: { label: 'Actif', tone: 'ok' },
  locked: { label: 'Verrouillé', tone: 'bad' },
}

/**
 * Les rôles **nominatifs** — tous sauf `team`, dérivés de `ROLES` pour qu'un 6ᵉ rôle ajouté au
 * modèle entre de lui-même au lieu d'être oublié par une liste écrite à la main.
 *
 * ⚠️ `agent` y est, alors que le prototype réserve « Utilisateurs & rôles » aux internes : c'est
 * le **seul** écran qui sache ouvrir un compte d'agent, et l'en exclure les rendrait impossibles à
 * créer autrement qu'en SQL. L'écran « Équipes » les **rattache** désormais à une `Team` (M22),
 * mais il ne les crée pas — les deux gestes sont distincts, et le rester est ce qui garde le
 * cycle de vie d'un compte au même endroit (M6).
 */
export const NOMINATIVE_ROLES: Exclude<Role, 'team'>[] = ROLES.filter(
  (role): role is Exclude<Role, 'team'> => role !== 'team',
)

/**
 * Le nom affiché d'un compte nominatif, **prénom d'abord** comme le prototype. Sans état civil
 * (possible, M6), le tiret : l'identifiant affiché à côté suffit, deviner un nom serait pire.
 */
export function fullNameOf(account: PublicUser): string {
  const parts = [account.firstName, account.lastName].filter(
    (part): part is string => part !== null && part.trim() !== '',
  )

  return parts.length === 0 ? '—' : parts.join(' ')
}

/** Notre phrase, pas celle du serveur : un `403` de rôle est le `Forbidden resource` de Nest. */
export const ACCOUNTS_REFUSED: ReadFailureNotice = {
  title: 'Réservé aux administrateurs',
  body: 'La gestion des comptes n’est ouverte qu’au rôle administrateur. Réessayer n’y changera rien : demandez à un administrateur d’intervenir.',
}
